import { Component, inject, OnInit } from '@angular/core';
import { PageHeaderComponent } from '../../components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '../../components/data-table-builder/data-table-builder.component';
import { ButtonModule } from 'primeng/button';
import { getCurrentTasksDataTableStructure } from './utils/current-tasks-data-table-object';
import { CurrentTasksService } from './current-tasks.service';
import { Tasks } from '@/types';
import { MessageService } from 'primeng/api';
import { DataTableRowActions, DynamicDataTable } from '../../components/data-table-builder/data-table.types';
import { CurrentTasksRepository } from '../../store/current-tasks-state/current-tasks-state.repository';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectOption } from '@/components/form-builder/form-builder.types';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { SpinnerComponent } from '../../components/shared/spinner/spinner.component';

@Component({
  selector: 'sm-current-tasks',
  imports: [PageHeaderComponent, DataTableBuilderComponent, ButtonModule, SelectModule, FormsModule, TranslateModule, SpinnerComponent],
  templateUrl: './current-tasks.component.html',
  styleUrl: './current-tasks.component.scss',
})
export class CurrentTasksComponent implements OnInit {
  //services
  currentTasksService = inject(CurrentTasksService);
  messageService = inject(MessageService);
  currentTasksRepository = inject(CurrentTasksRepository);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);

  //breadcrumb
  breadcrumb = [
    {
      label: 'Activités récentes',
      routerLink: ['/current-tasks'],
    },
  ];

  //states

  //filter
  types: SelectOption[] = [
    { code: 'AGPM', name: 'Avis général de passation de marché (AGPM)' },
    { code: 'PAA', name: 'Plan annuel des achats de cette année (PAA)' },
    { code: 'PPM', name: 'Plan de passation marché (PPM)' },
    { code: 'DAC', name: "Dossier d'appel à concurrence (DAC)" },
    { code: 'Eclairecissement', name: "Demandes d'éclaircissement" },
    { code: 'ReportRequest', name: "Demandes de report" },
  ];
  selectedType: SelectOption = this.types[0];

  //data-table
  rowActionsFn?: () => DataTableRowActions;
  dataTableObject?: DynamicDataTable;
  loading = new BehaviorSubject<boolean>(true);
  data?: Tasks;

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(params => {
      const typeCode = params['type'];
      const foundType = this.types.find(type => type.code === typeCode);
      this.selectedType = foundType ?? this.types[0];
      this.loadData();
      this.initDatatable();
    });
  }

  onSelectChange(value: SelectOption) {
    this.selectedType = value;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { type: value.code },
      queryParamsHandling: 'merge',
    });
  }

  initDatatable() {
    switch (this.selectedType.code) {
      case 'AGPM':
        this.rowActionsFn = () => this.getAgpmRowActions();
        break;

      case 'PAA':
        this.rowActionsFn = () => this.getPaaRowActions();
        break;

      case 'Eclairecissement':
        this.rowActionsFn = () => this.getEclaircissementRowActions();
        break;

      case 'DAC':
        this.rowActionsFn = () => this.getDacRowActions();
        break;

      case 'PPM':
        this.rowActionsFn = () => this.getPpmRowActions();
        break;

      case 'ReportRequest':
        this.rowActionsFn = () => this.getReportRequestRowActions();
        break;

      default:
        this.rowActionsFn = () => ({});
        break;
    }

    this.dataTableObject = getCurrentTasksDataTableStructure({
      store: this.currentTasksRepository,
      rowActionsFn: this.rowActionsFn,
    });
  }

  loadData() {
    this.loading.next(true);
    const next = (data: any) => {
      data.tasks =
        data.tasks &&
        data.tasks.map((task: any) => {
          return {
            ...task,
            tenantId: data.tenantId,
          };
        });
      this.data = data;
      // Move these after data is set
      this.initDatatable();
      this.loading.next(false);
    };

    const error = () => {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Une erreur est survenue lors de la récupération des activités',
      });
      this.loading.next(false);
    };

    if (this.selectedType.code === 'AGPM') {
      this.currentTasksService.getUserAgpmTasks().subscribe({ next, error });
    } else if (this.selectedType.code === 'PAA') {
      this.currentTasksService.getUserPaaTasks().subscribe({ next, error });
    } else if (this.selectedType.code === 'PPM') {
      this.currentTasksService.getUserPpmTasks().subscribe({ next, error });
    } else if (this.selectedType.code === 'DAC') {
      this.currentTasksService.getUserDacTasks().subscribe({ next, error });
    } else if (this.selectedType.code === 'Eclairecissement') {
      this.currentTasksService.getUserEclaircissementTasks().subscribe({ next, error });
    } else if (this.selectedType.code === 'ReportRequest') {
      this.currentTasksService.getUserReportRequestTasks().subscribe({ next, error });
    }
  }

  getAgpmRowActions(): DataTableRowActions {
    return {
      defaultActions: {
        edit: {
          label: 'ppm.actions.treat',
        },
      },
      editAction: () => {
        this.router.navigate([`agpm-current-task/${this.currentTasksRepository.getNested<number>('response', 'taskId')}`]);
      },
    };
  }

  getPaaRowActions(): DataTableRowActions {
    return {
      defaultActions: {
        edit: {
          label: 'paa.actions.treat',
        },
      },
      editAction: () => {
        this.router.navigate([`paa-current-task/${this.currentTasksRepository.getNested<number>('response', 'taskId')}`]);
      },
    };
  }

  getPpmRowActions(): DataTableRowActions {
    return {
      defaultActions: {
        edit: {
          label: 'ppm.actions.treat',
        },
      },
      editAction: () => {
        this.router.navigate([`ppm-current-task/${this.currentTasksRepository.getNested<number>('response', 'taskId')}`]);
      },
    };
  }

  getDacRowActions(): DataTableRowActions {
    return {
      defaultActions: {
        edit: {
          label: 'dac.actions.treat',
        },
      },
      editAction: () => {
        this.router.navigate([`dac-current-task/${this.currentTasksRepository.getNested<number>('response', 'taskId')}`]);
      },
    };
  }

  getEclaircissementRowActions(): DataTableRowActions {
    return {
      defaultActions: {
        edit: {
          label: 'clarification.actions.treat',
        },
      },
      editAction: () => {
        this.router.navigate([`outputs/${this.currentTasksRepository.getNested<number>('response', 'id')}`]);
      },
    };
  }

  getReportRequestRowActions(): DataTableRowActions {
    return {
      defaultActions: {
        edit: {
          label: 'table.actions.treat',
        },
      },
      editAction: () => {
        this.router.navigate([`report-request/${this.currentTasksRepository.getNested<number>('response', 'id')}`]);
      },
    };
  }
}
